// HomeLiveStreamFeed.jsx
import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import { LIVESTREAM_FEED } from '../RemoteConfigKeys.js';
import { InfiniteScroller, ScrollItem } from './InfiniteScroller.jsx';
import WithIntersectionObserver from './WithIntersectionObserver.jsx';
import media, { hover } from '../style/media.js';
import { color, textColor, loadingIconOpacity } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { livestreamAdType as livestreamAdTypeConstant } from '../resource/liveStreamConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { QueryType } from '../resource/feedConstants.js';
import HomeLiveStreamAd from '../container/HomeLiveStreamAd.js';
import SortButton from '../container/SortButton.js';
import FilterButton from '../container/FilterButton.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import ToggleSwitch from '../component/ToggleSwitch.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import withRouter from '../component/WithRouter.jsx';
import FeedFilterPanelV2 from '../container/FeedFilterPanelV2.js';
import UserHashtagCard from '../container/UserHashtagCard.js';

import TextEllipsis from '../style/TextEllipsis.js';
import { removeLangFromPath } from '../resource/langPath.js';

import LoadingIconResource from '../../img/img-loading-grey-20.svg';
import EmptyLivestreamIconSource from '../../img/img_no_live.svg';

export class HomeLiveStreamFeed extends React.PureComponent {
  constructor(props) {
    super(props);
    // fetch data for SSR
    const {
      staticContext,
      isRemoteConfigMerged,
      isFirstPageFetched,
      feedNameWithQuery,
      fetchFeeds,
    } = props;
    if (staticContext.actions) {
      if (isRemoteConfigMerged && !isFirstPageFetched) {
        staticContext.actions.push({
          function: fetchFeeds,
          argumentObject: {
            type: feedNameWithQuery,
            page: 1,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
    }
  }

  retryCount = 0;
  intervalId = null;
  pendingFetch = null;
  state = {
    shouldAutoRenewFeed: false,
  };

  getLatestFeedRenewUnix = () => {
    const { feedRenewObject } = this.props;
    return Object.values(feedRenewObject)
      .filter(item => item.timestamp != null)
      .sort((a, b) => b.timestamp - a.timestamp)[0]?.timestamp;
  };

  fetchFeeds = ({
    feedNameWithQuery,
    page = 1,
    shouldSkipLocalCache = false,
  } = {}) => {
    const {
      shouldRefresh,
      isRemoteConfigMerged,
      renewFeedId,
      latestFeedRenewUnix,
      fetchFeeds,
    } = this.props;
    if (feedNameWithQuery) {
      if (isRemoteConfigMerged) {
        fetchFeeds({
          id: renewFeedId,
          type: feedNameWithQuery,
          page,
          unixTimestamp: latestFeedRenewUnix,
          shouldSkipLocalCache: shouldSkipLocalCache || shouldRefresh,
        });
      } else {
        this.pendingFetch = () =>
          fetchFeeds({
            id: renewFeedId,
            type: feedNameWithQuery,
            page,
            unixTimestamp: latestFeedRenewUnix,
            shouldSkipLocalCache: shouldRefresh,
          });
      }
    }
  };
  subscribeFeedChannel = ({
    intersectedFeedName = '',
    prevIntersectedFeedName = '',
  } = {}) => {
    const { subscribeFeedChannel, unsubscribeFeedChannel } = this.props;
    const intersectedFeedNames = intersectedFeedName.split('&');
    const prevIntersectedFeedNames = prevIntersectedFeedName.split('&');
    const toUnsubscribeFeedNames = prevIntersectedFeedNames.filter(
      name => !intersectedFeedNames.includes(name)
    );
    const toSubscribeFeedNames = intersectedFeedNames.filter(
      name => !prevIntersectedFeedNames.includes(name)
    );
    toUnsubscribeFeedNames.forEach(feedName =>
      unsubscribeFeedChannel({ feedName })
    );
    toSubscribeFeedNames.forEach(feedName =>
      subscribeFeedChannel({ feedName })
    );
  };
  componentDidMount() {
    const {
      feedNameWithQuery,
      intersectedFeedName,
      isFirstPageFetched,
      defaultFeedNameWithQuery,
      hasDefaultFeedFetched,
      isLivestreamFeedDeduped,
      nextPage,
      isNextPageFetching,
      userIds,
    } = this.props;

    // Feed fetching logic:
    // - Initial: Fetch first page if no feeds exist
    // - Fetched: Preserve existing feeds for better UX
    //    1. Maintains scroll position
    //    2. Shows cached content immediately
    if (!isFirstPageFetched) {
      this.fetchFeeds({
        feedNameWithQuery,
        page: 1,
        shouldSkipLocalCache: true,
      });
    }

    // Auto fetch next page when top feed takes all items
    const shouldFetchNextPage =
      isLivestreamFeedDeduped &&
      isFirstPageFetched &&
      !userIds.length &&
      nextPage &&
      !isNextPageFetching;
    if (shouldFetchNextPage) {
      this.fetchFeeds({
        feedNameWithQuery,
        page: nextPage,
      });
      return;
    }

    // Fetch default feed if user enter livestream page with any filter query
    if (
      feedNameWithQuery !== defaultFeedNameWithQuery &&
      !hasDefaultFeedFetched
    ) {
      this.fetchFeeds({
        feedNameWithQuery: defaultFeedNameWithQuery,
        page: 1,
        shouldSkipLocalCache: true,
      });
    }
    this.subscribeFeedChannel({
      intersectedFeedName,
    });
  }
  componentDidUpdate(prevProps) {
    const {
      isAuthed,
      isRemoteConfigMerged,
      feedNameWithQuery,
      intersectedFeedName,
      shouldUseCuratorMode,
      renewFeedId,
      shouldRefresh,
      isFirstPageFetched,
      userIds,
      category,
      sendUserDebugFeedContentError,
    } = this.props;
    if (intersectedFeedName !== prevProps.intersectedFeedName) {
      this.subscribeFeedChannel({
        intersectedFeedName,
        prevIntersectedFeedName: prevProps.intersectedFeedName,
      });
    }
    if (
      isAuthed !== prevProps.isAuthed ||
      isRemoteConfigMerged !== prevProps.isRemoteConfigMerged ||
      feedNameWithQuery !== prevProps.feedNameWithQuery
    ) {
      if (feedNameWithQuery !== prevProps.feedNameWithQuery) {
        this.fetchFeeds({ feedNameWithQuery, page: 1 });
      } else if (this.pendingFetch) {
        this.pendingFetch();
        this.pendingFetch = null;
      }
    }

    if (renewFeedId !== prevProps.renewFeedId && shouldUseCuratorMode) {
      const { shouldAutoRenewFeed } = this.state;
      if (shouldAutoRenewFeed) this.fetchFeeds({ feedNameWithQuery, page: 1 });
    }

    if (shouldRefresh && shouldRefresh !== prevProps.shouldRefresh) {
      this.fetchFeeds({ feedNameWithQuery, page: 1 });
    }

    // Auto fetch next page when top feed takes all items
    const { isLivestreamFeedDeduped, nextPage, isNextPageFetching } =
      this.props;
    const shouldFetchNextPage =
      isLivestreamFeedDeduped &&
      isFirstPageFetched &&
      !userIds.length &&
      nextPage &&
      !isNextPageFetching;
    if (shouldFetchNextPage) {
      this.fetchFeeds({
        feedNameWithQuery,
        page: nextPage,
      });
      return;
    }

    const shouldRetry =
      isRemoteConfigMerged &&
      isFirstPageFetched &&
      !userIds.length &&
      !isLivestreamFeedDeduped &&
      this.retryCount < 10;
    if (shouldRetry) {
      this.fetchFeeds({
        feedNameWithQuery,
        page: 1,
        shouldSkipLocalCache: true,
      });
      this.retryCount++;
      sendUserDebugFeedContentError({
        category,
        baseRemoteConfigKeyName: LIVESTREAM_FEED,
      });
    }
  }
  componentWillUnmount() {
    clearInterval(this.intervalId);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'HomeLiveStreamFeed';
    }
  }

  renderLoading = () => {
    return (
      <LoadingWrapper>
        <LoadingIcon src={LoadingIconResource} alt="loading icon" />
      </LoadingWrapper>
    );
  };

  renderHeader = () => {
    const {
      location,
      t,
      category,
      titleI18nId,
      shouldUseCuratorMode,
      filterType,
    } = this.props;
    const { shouldAutoRenewFeed } = this.state;

    switch (filterType) {
      case 'v1':
      default:
        return (
          <Header>
            <HeaderLeft>
              <Title>
                {t(titleI18nId, {
                  ns: TranslationNamespace.FEED,
                })}
              </Title>
            </HeaderLeft>
            <HeaderRight>
              <ConditionsWrapper>
                <FilterButton
                  remoteConfigKeyName={LIVESTREAM_FEED}
                  baseFeedName={category}
                  mixpanelButtonId={ButtonId.LiveStream.ButtonLivestreamFilter}
                />
                <SortButton
                  remoteConfigKeyName={LIVESTREAM_FEED}
                  baseFeedName={category}
                  mixpanelButtonId={ButtonId.LiveStream.ButtonLivestreamSorting}
                  defaultItemI18nKey={'livestream_default_sorting'}
                />
                {shouldUseCuratorMode && (
                  <AutoUpdateButton>
                    <ToggleSwitch
                      active={shouldAutoRenewFeed}
                      onToggle={() => {
                        this.setState({
                          shouldAutoRenewFeed: !shouldAutoRenewFeed,
                        });
                      }}
                    />
                    <span>
                      {t('button_update', {
                        ns: TranslationNamespace.FEED,
                      })}
                    </span>
                  </AutoUpdateButton>
                )}
              </ConditionsWrapper>
            </HeaderRight>
          </Header>
        );
      case 'v2': {
        const cleanPathname = removeLangFromPath(location.pathname);
        return (
          <>
            {cleanPathname === '/following' && (
              <Header style={{ marginTop: '12px' }}>
                <HeaderLeft>
                  <Title>
                    {t(titleI18nId, {
                      ns: TranslationNamespace.FEED,
                    })}
                  </Title>
                </HeaderLeft>
              </Header>
            )}
            <FeedFilterPanelV2 baseFeedName={category} />
          </>
        );
      }
    }
  };

  renderFilterEmptyResult = () => {
    const {
      category,
      filterType,
      t,
      setSelectedFeedQueryOption,
      resetSelectedOptions,
    } = this.props;

    switch (filterType) {
      case 'v1':
      default:
        return (
          <QueryResultEmptyWrapper>
            <QueryEmptyIcon
              src={EmptyLivestreamIconSource}
              alt="empty livestream icon"
            />
            <EmptyFilterWording>
              {t('filter_content_no_found', {
                ns: TranslationNamespace.FEED,
              })}
            </EmptyFilterWording>
            <EmptyFilterWording>
              {t('or', {
                ns: TranslationNamespace.GENERAL,
              })}
            </EmptyFilterWording>
            <ResetFilterButton
              data-element_id={ButtonId.LiveStream.ButtonLivestreamResetFilter}
              onClick={() => {
                return setSelectedFeedQueryOption({
                  queryType: QueryType.FILTER,
                  remoteConfigKeyName: LIVESTREAM_FEED,
                  baseFeedName: category,
                });
              }}
            >
              {t('reset_filter', {
                ns: TranslationNamespace.FEED,
              })}
            </ResetFilterButton>
          </QueryResultEmptyWrapper>
        );
      case 'v2':
        return (
          <EmptyFilterWrapper>
            <EmptyFilterTitle>
              {t('filter_content_no_found_title', {
                ns: TranslationNamespace.FEED,
              })}
            </EmptyFilterTitle>
            <EmptyFilterDescription>
              {t('filter_content_no_found_desc', {
                ns: TranslationNamespace.FEED,
              })}
            </EmptyFilterDescription>
            <ResetFilterButtonV2
              data-element_id={ButtonId.All.ClearFilterOptions}
              onClick={() =>
                resetSelectedOptions({
                  baseFeedName: category,
                })
              }
            >
              {t('button_filter_clear', {
                ns: TranslationNamespace.FEED,
              })}
            </ResetFilterButtonV2>
          </EmptyFilterWrapper>
        );
    }
  };

  renderEmptyResult = () => {
    const { location, t } = this.props;
    const cleanPathname = removeLangFromPath(location.pathname);

    return (
      <EmptyFilterWrapper>
        <EmptyLivestreamIcon
          src={EmptyLivestreamIconSource}
          alt="empty livestream icon"
        />
        <EmptyResultTitle>
          {t('livestream_no_content_title', {
            ns: TranslationNamespace.FEED,
          })}
        </EmptyResultTitle>
        <LinkButton
          to={cleanPathname === '/following' ? '/' : '/category-overview'}
        >
          {t(
            cleanPathname === '/following'
              ? 'button_explore_more'
              : 'button_explore_category',
            {
              ns: TranslationNamespace.FEED,
            }
          )}
        </LinkButton>
      </EmptyFilterWrapper>
    );
  };

  renderFeeds = ({
    feedNameWithQuery,
    userIds,
    nextPage,
    isNextPageFetching,
  }) => {
    const { location, isSquare, shouldUseCuratorMode } = this.props;
    const cleanPathname = removeLangFromPath(location.pathname);
    const isLivestreamPage = cleanPathname === '/livestream';

    return (
      <InfiniteScroller
        pageStart={1}
        hasMore={nextPage !== null}
        loadMore={() => {
          nextPage
            ? this.fetchFeeds({
                feedNameWithQuery,
                page: nextPage,
              })
            : null;
        }}
        isFetching={isNextPageFetching}
        threshold={100}
      >
        <LiveStreamCardsWrapper isLivestreamPage={isLivestreamPage}>
          <ScrollItem loader={this.renderLoading()}>
            {userIds.map((userId, index) => {
              if (isSquare && !shouldUseCuratorMode) {
                if (
                  [
                    livestreamAdTypeConstant.SWAGGER,
                    livestreamAdTypeConstant.ADVERTISER,
                  ].includes(userId)
                ) {
                  return shouldUseCuratorMode ? null : ( // skip ad
                    <div key={userId}>
                      <HomeLiveStreamAd adType={userId} />
                    </div>
                  );
                }
                return (
                  <UserHashtagCard
                    key={userId}
                    userId={userId}
                    category={feedNameWithQuery}
                    index={index}
                  />
                );
              } else {
                if (
                  [
                    livestreamAdTypeConstant.SWAGGER,
                    livestreamAdTypeConstant.ADVERTISER,
                  ].includes(userId)
                ) {
                  return shouldUseCuratorMode ? null : ( // skip ad
                    <HomeLiveStreamAd key={userId} adType={userId} />
                  );
                }
                return (
                  <WithIntersectionObserver key={userId} shouldKeepObserve>
                    {({ isIntersecting }) => (
                      <LiveStreamCardWrapper>
                        {shouldUseCuratorMode ? (
                          <UniversalComponent
                            filename="Container_LiveStreamCuratorCard"
                            userId={userId}
                            streamId={userId}
                            isIntersecting={isIntersecting}
                            importer={
                              /* #__LOADABLE__ */ () =>
                                import('../container/LiveStreamCuratorCard.js')
                            }
                          />
                        ) : (
                          <UniversalComponent
                            filename="Container_LiveStreamCard"
                            userId={userId}
                            streamId={userId}
                            isIntersecting={isIntersecting}
                            category={feedNameWithQuery}
                            index={index}
                            categoryId={feedNameWithQuery}
                            id={userId}
                            itemIndexInCategory={index}
                            importer={
                              /* #__LOADABLE__ */ () =>
                                import('../container/LiveStreamCard.js')
                            }
                          />
                        )}
                      </LiveStreamCardWrapper>
                    )}
                  </WithIntersectionObserver>
                );
              }
            })}
          </ScrollItem>
        </LiveStreamCardsWrapper>
      </InfiniteScroller>
    );
  };

  renderEmptyRecommendLivestreams = () => {
    const {
      location,
      filterType,
      totalCount,
      defaultFeedNameWithQuery,
      defaultUserIds,
      defaultNextPage,
      isDefaultNextPageFetching,
      t,
    } = this.props;
    const cleanPathname = removeLangFromPath(location.pathname);

    const shouldShowRecommendLivestreams =
      filterType === 'v2' && cleanPathname === '/livestream' && totalCount <= 6;

    return (
      shouldShowRecommendLivestreams && (
        <>
          <RecommendText>
            {t('livestream_recommendation_you_may_like', {
              ns: TranslationNamespace.FEED,
            })}
          </RecommendText>
          {this.renderFeeds({
            feedNameWithQuery: defaultFeedNameWithQuery,
            userIds: defaultUserIds,
            nextPage: defaultNextPage,
            isNextPageFetching: isDefaultNextPageFetching,
          })}
        </>
      )
    );
  };

  render() {
    const {
      category,
      feedNameWithQuery,
      userIds,
      nextPage,
      selectedFilterOptions,
      isFirstPageFetching,
      isFirstPageFetched,
      isNextPageFetching,
      livestreamFeed,
      livestreamFeedSortings,
    } = this.props;
    const isLoading = isFirstPageFetching && userIds?.length === 0;
    const isEmpty = !isLoading && userIds.length === 0;

    return (
      <StyledHomeLiveStreamFeed
        data-category={category}
        data-category_with_query={feedNameWithQuery}
        data-livestream_feed={livestreamFeed}
        data-livestream_feed_sortings={livestreamFeedSortings.join(',')}
      >
        {this.renderHeader()}
        {isLoading
          ? this.renderLoading()
          : selectedFilterOptions.length > 0 && isEmpty
            ? this.renderFilterEmptyResult()
            : isFirstPageFetched && !userIds.length
              ? this.renderEmptyResult()
              : !isFirstPageFetched && !userIds.length
                ? this.renderLoading()
                : this.renderFeeds({
                    feedNameWithQuery,
                    userIds,
                    nextPage,
                    isNextPageFetching,
                  })}
        {!isLoading && this.renderEmptyRecommendLivestreams()}
      </StyledHomeLiveStreamFeed>
    );
  }
}

HomeLiveStreamFeed.propTypes = {
  location: PropTypes.object,
  staticContext: PropTypes.object,
  isAuthed: PropTypes.bool,
  isLivestreamFeedDeduped: PropTypes.bool,
  isRemoteConfigMerged: PropTypes.bool,
  category: PropTypes.string,
  titleI18nId: PropTypes.string,
  feedNameWithQuery: PropTypes.string,
  intersectedFeedName: PropTypes.string,
  renewFeedId: PropTypes.string,
  latestFeedRenewUnix: PropTypes.number,
  totalCount: PropTypes.number,
  userIds: PropTypes.array,
  selectedFilterOptions: PropTypes.array,
  nextPage: PropTypes.number,
  isNextPageFetching: PropTypes.bool,
  isFirstPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  defaultFeedNameWithQuery: PropTypes.string,
  defaultUserIds: PropTypes.array,
  defaultNextPage: PropTypes.number,
  isDefaultNextPageFetching: PropTypes.bool,
  hasDefaultFeedFetched: PropTypes.bool,
  isSquare: PropTypes.bool,
  feedRenewObject: PropTypes.object,
  fetchFeeds: PropTypes.func,
  subscribeFeedChannel: PropTypes.func,
  unsubscribeFeedChannel: PropTypes.func,
  setSelectedFeedQueryOption: PropTypes.func,
  t: PropTypes.func.isRequired,
  shouldUseCuratorMode: PropTypes.bool,
  livestreamFeed: PropTypes.string,
  livestreamFeedSortings: PropTypes.array,
  filterType: PropTypes.string,
  sendUserDebugFeedContentError: PropTypes.func,
  resetSelectedOptions: PropTypes.func,
};

HomeLiveStreamFeed.defaultProps = {
  location: null,
  staticContext: {},
  isAuthed: false,
  isLivestreamFeedDeduped: false,
  isRemoteConfigMerged: false,
  category: null,
  titleI18nId: '',
  feedNameWithQuery: null,
  intersectedFeedName: '',
  renewFeedId: null,
  latestFeedRenewUnix: null,
  totalCount: 0,
  userIds: [],
  selectedFilterOptions: [],
  nextPage: null,
  isNextPageFetching: false,
  isFirstPageFetching: false,
  isFirstPageFetched: false,
  shouldRefresh: false,
  defaultFeedNameWithQuery: '',
  defaultUserIds: [],
  defaultNextPage: null,
  isDefaultNextPageFetching: false,
  hasDefaultFeedFetched: false,
  isSquare: false,
  feedRenewObject: {},
  fetchFeeds: () => null,
  subscribeFeedChannel: () => null,
  unsubscribeFeedChannel: () => null,
  setSelectedFeedQueryOption: () => null,
  shouldUseCuratorMode: false,
  livestreamFeed: '',
  livestreamFeedSortings: [],
  filterType: 'v1',
  sendUserDebugFeedContentError: () => null,
  resetSelectedOptions: () => null,
};

const StyledHomeLiveStreamFeed = styled.div``;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px;
`;

const HeaderLeft = styled.div`
  flex: auto;
  overflow: hidden;
`;

const Title = styled.h2`
  ${TextEllipsis}
  margin: 0;
  display: block;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
  ${media.mobile`
    font-size: 18px;
  `}
`;

const HeaderRight = styled.div`
  margin-left: 8px;
  flex: none;
`;

const ConditionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  > * {
    :first-child {
      margin-left: 0px;
    }
    margin-left: 8px;
  }
  :empty {
    padding: 0px;
    margin-bottom: 20px;
    ${media.mobile`
      margin-bottom: 12px;
    `};
  }
`;

const LiveStreamCardsWrapper = styled.div`
  padding: 12px 16px;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  ${media.laptop`
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  `}
  ${media.tablet`
    gap: 8px;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  `}
  ${media.mobile`
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  `}

  ${({ isLivestreamPage }) =>
    isLivestreamPage &&
    css`
      padding: 0px 16px 12px;
    `}
`;

const LiveStreamCardWrapper = styled.div``;

const LoadingWrapper = styled.div`
  padding-top: 50px;
  display: flex;
  justify-content: center;
  opacity: ${loadingIconOpacity};
`;

const LoadingIcon = styled.img`
  width: 32px;
  height: auto;
`;

const QueryResultEmptyWrapper = styled.div`
  margin-top: 48px;
  text-align: center;
  font-size: 16px;
  color: ${textColor.grey};

  ${media.mobile`
    font-size: 14px;
  `};
`;

const EmptyFilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 40px 0;
  padding: 0 16px;
`;

const EmptyFilterTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
  color: ${textColor.white};
`;

const EmptyFilterDescription = styled.div`
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: ${textColor.grey};
`;

const EmptyResultTitle = styled.div`
  font-size: 18px;
  line-height: 1.5;
  color: ${color.grey[300]};
`;

const BaseButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  margin-top: 12px;
  padding: 2px 8px;
  border: 1px solid ${color.tealBlue};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.tealBlue};
  transition: 0.3s;

  ${hover`
    border-color: ${color.tealBlue_alpha_80};
  `}

  &:active {
    background: ${color.tealBlue_alpha_40};
    border-color: ${color.tealBlue};
  }
`;

const ResetFilterButtonV2 = styled(BaseButton)``;
const LinkButton = styled(BaseButton).attrs({
  as: Link,
})`
  margin-top: 16px;
`;

const RecommendText = styled.p`
  margin: 8px 0;
  padding: 0 16px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
  color: ${textColor.white};
`;

const QueryEmptyIcon = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
  ${media.mobile`
    margin-bottom: 8px;
  `};
`;

const EmptyLivestreamIcon = styled.img`
  margin-bottom: 16px;
  width: 100px;
  height: 100px;
  object-fit: contain;
`;

const EmptyFilterWording = styled.div`
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 12px;
  max-width: 260px;
  line-height: 1.6;
  white-space: pre-wrap;
  ${media.mobile`
    margin-bottom: 8px;
  `};
`;

const ResetFilterButton = styled.button`
  ${ResetButtonStyle};
  margin: 0px auto;
  border: 1px solid ${color.white};
  border-radius: 4px;
  padding: 0px 8px;
  height: 36px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${textColor.white};
  ${media.mobile`
    height: 28px;
    font-size: 12px;
  `};
`;

const AutoUpdateButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 4px;
  > span {
    margin-left: 4px;
  }
`;

export default withRouter(withTranslation()(HomeLiveStreamFeed));
